import { Component, EventEmitter, Input, Output } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ProdutoDesenho, ProdutoDesenhoConsulta } from 'src/app/shared/interfaces/produto/produto-desenho.interface';
import { ProdutoAPIService } from 'src/app/modules/produto/services/produto-api.service';
import * as bootstrap from 'bootstrap';

/** Desenho já listado na tela de quem abriu (gravado ou ainda não). */
export interface DesenhoNaTela {
  numeroDesenho: string;
  versao?: string | null;
  isDeleted?: boolean;
}

/**
 * Cadastro de um desenho de produto.
 *
 * <p>Emite o desenho preenchido; quem abriu decide o que fazer com ele. Na tela
 * de produto vai para o grid e só é gravado no Salvar; no pedido e no
 * pré-pedido, idem — nada aqui chama API.</p>
 *
 * <p>Versão: obrigatória e sempre calculada, nunca digitada. Ao ler o PDF (que preenche o
 * número) ou ao sair do campo do número, a modal consulta a API: número de outro produto não pode;
 * número que já existe neste produto (mesmo desativado) recebe a maior versão + 1; número novo
 * recebe 1. O campo fica travado. A API confere de novo ao salvar.</p>
 *
 * <p>O `idModal` existe porque a mesma tela pode ter mais de uma instância
 * (a do grid de itens e a do modal de novo item, por exemplo), e dois
 * elementos com o mesmo id fazem o Bootstrap abrir sempre o primeiro.</p>
 */
@Component({
  selector: 'app-modal-desenho',
  templateUrl: './modal-desenho.component.html',
  styleUrl: './modal-desenho.component.scss'
})
export class ModalDesenhoComponent {
  @Input() idModal: string = 'modal-desenho';

  /** Produto a que o desenho pertence. Zero quando o produto ainda não existe. */
  @Input() produtoId: number = 0;

  /** Texto do cabeçalho — a tela diz de qual produto se trata. */
  @Input() subtitulo: string = '';

  /**
   * PDF obrigatório. Falso no pré-pedido, onde a planilha pode trazer o número
   * do desenho antes de o arquivo existir.
   */
  @Input() exigirArquivo: boolean = true;

  /**
   * Desenhos que a tela já lista (inclusive os ainda não gravados): entram no cálculo da próxima
   * versão junto com o que a API devolve.
   */
  @Input() desenhosNaTela: DesenhoNaTela[] = [];

  @Output() confirmado = new EventEmitter<ProdutoDesenho>();

  desenho: ProdutoDesenho = this.novo();
  arquivo: File | null = null;
  erro: string = '';
  /** Aviso da consulta do número ("já existe na versão X"). */
  aviso: string = '';
  consultando = false;
  /** Número (normalizado) da última consulta que deu certo, e se ele pode ser usado. */
  private numeroConsultado: string | null = null;
  private numeroLiberado = false;
  /** Conta as consultas: resposta de uma consulta antiga (ou de antes de reabrir a modal) é descartada. */
  private consultaSeq = 0;

  constructor(private produtoApi: ProdutoAPIService) {}

  abrirModal(): void {
    this.consultaSeq++;
    this.consultando = false;
    this.desenho = this.novo();
    this.arquivo = null;
    this.erro = '';
    this.aviso = '';
    this.numeroConsultado = null;
    this.numeroLiberado = false;
    this.limparArquivo();

    const el = document.getElementById(this.idModal);
    if (el) new bootstrap.Modal(el).show();
  }

  fecharModal(): void {
    const el = document.getElementById(this.idModal);
    if (!el) return;

    const modal = bootstrap.Modal.getInstance(el);
    if (modal) modal.hide();

    this.limparArquivo();
  }

  onArquivoSelecionado(event: any): void {
    const file: File = event.target.files[0];

    if (!file) {
      this.arquivo = null;
      return;
    }

    const ehPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!ehPdf) {
      this.erro = 'O desenho precisa ser um PDF.';
      this.arquivo = null;
      (event.target as HTMLInputElement).value = '';
      return;
    }

    if (file.size === 0) {
      this.erro = 'O arquivo está vazio.';
      this.arquivo = null;
      (event.target as HTMLInputElement).value = '';
      return;
    }

    // 10MB, o mesmo limite do anexo de produto.
    if (file.size > 10 * 1024 * 1024) {
      this.erro = 'Arquivo muito grande. Máximo de 10MB.';
      this.arquivo = null;
      (event.target as HTMLInputElement).value = '';
      return;
    }

    this.erro = '';
    this.arquivo = file;

    // O nome do PDF quase sempre é o próprio número do desenho. Só preenche o
    // que está vazio: quem já digitou o número decidiu, e sobrescrever aqui
    // apagaria a decisão sem avisar.
    if (!(this.desenho.numeroDesenho || '').trim()) {
      this.desenho.numeroDesenho = this.numeroDoNomeDoArquivo(file.name);
      this.numeroAlterado();
    }
    this.consultarNumero();
  }

  /** Digitou no número: a versão calculada para o número anterior deixa de valer. */
  numeroAlterado(): void {
    if (this.chave(this.desenho.numeroDesenho) === this.numeroConsultado) return;
    this.desenho.versao = null;
    this.aviso = '';
    this.numeroConsultado = null;
    this.numeroLiberado = false;
  }

  /**
   * Ao sair do campo (e ao ler o PDF): pergunta à API pelo número e calcula a versão.
   * Devolve se o número pode ser usado.
   */
  async consultarNumero(): Promise<boolean> {
    const numero = (this.desenho.numeroDesenho || '').trim();
    if (!numero) return false;

    const chave = this.chave(numero);
    if (chave === this.numeroConsultado) return this.numeroLiberado;

    const seq = ++this.consultaSeq;
    // A resposta só vale se nada mudou enquanto ela vinha: nem outra consulta, nem a modal
    // reaberta, nem o número digitado. Senão a versão de um número cairia no outro.
    const valida = () => seq === this.consultaSeq && this.chave(this.desenho.numeroDesenho) === chave;

    this.consultando = true;
    try {
      const consulta = await firstValueFrom(this.produtoApi.consultarNumeroDesenho(numero, this.produtoId));
      if (!valida()) return false;
      this.aplicarConsulta(numero, consulta);
      this.numeroConsultado = chave;
      return this.numeroLiberado;
    } catch {
      if (!valida()) return false;
      this.erro = 'Não foi possível conferir o número do desenho. Tente de novo.';
      this.desenho.versao = null;
      this.numeroConsultado = null;
      this.numeroLiberado = false;
      return false;
    } finally {
      if (seq === this.consultaSeq) this.consultando = false;
    }
  }

  private aplicarConsulta(numero: string, consulta: ProdutoDesenhoConsulta): void {
    if (consulta.outroProdutoId) {
      const produto = [consulta.outroProdutoIdentificacao, consulta.outroProdutoDescricao]
        .filter(s => !!s).join(' - ');
      this.erro = `O desenho ${numero} já está cadastrado em outro produto (${produto}).`;
      this.aviso = '';
      this.desenho.versao = null;
      this.numeroLiberado = false;
      return;
    }

    // A maior versão entre o banco e o que a tela já tem (desenho novo ainda não gravado).
    const daTela = this.desenhosNaTela
      .filter(d => !d.isDeleted && this.chave(d.numeroDesenho) === this.chave(numero))
      .map(d => this.versaoNumero(d.versao));
    const ultima = Math.max(consulta.ultimaVersao ?? 0, ...daTela, 0);
    const proxima = ultima + 1;

    this.erro = '';
    this.desenho.versao = String(proxima);
    this.aviso = ultima > 0
      ? `Já existe o desenho ${numero} na versão ${ultima}. Este será cadastrado como versão ${proxima}.`
      : '';
    this.numeroLiberado = true;
  }

  /** Mesma conta da API (ProdutoDesenhoVersao): o primeiro número do texto; sem número = 0. */
  private versaoNumero(versao: string | null | undefined): number {
    const m = (versao || '').match(/[0-9]{1,9}/);
    return m ? parseInt(m[0], 10) : 0;
  }

  private chave(numero: string | null | undefined): string {
    return (numero || '').trim().toUpperCase();
  }

  /**
   * Número do desenho a partir do nome do arquivo: tira a extensão e os espaços
   * das pontas. O resto do nome fica como está — a convenção de cada cliente é
   * dele, e "limpar" mais do que isso inventaria número que ninguém reconhece.
   */
  private numeroDoNomeDoArquivo(nome: string): string {
    const semExtensao = nome.replace(/\.pdf$/i, '').trim();

    // O campo é VARCHAR(200) no banco; cortar aqui evita o erro do Postgres no
    // lugar de um 400 legível.
    return semExtensao.slice(0, 200);
  }

  async confirmar(): Promise<void> {
    const numero = (this.desenho.numeroDesenho || '').trim();

    if (!numero) {
      this.erro = 'Informe o número do desenho.';
      return;
    }

    if (numero.length > 200) {
      this.erro = 'O número do desenho tem no máximo 200 caracteres.';
      return;
    }

    // Clicou em Adicionar sem sair do campo: consulta agora. Número de outro produto não passa.
    if (!(await this.consultarNumero())) return;

    if (!this.versaoNumero(this.desenho.versao)) {
      this.erro = 'A versão do desenho é obrigatória.';
      return;
    }

    if (this.exigirArquivo && !this.arquivo) {
      this.erro = 'Anexe o PDF do desenho.';
      return;
    }

    if (!this.arquivo) {
      this.emitir(numero, null, null);
      return;
    }

    // O arquivo viaja em base64 dentro do produto, como os anexos já fazem —
    // gravar só acontece no Salvar da tela.
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      this.emitir(numero, base64, this.arquivo!.name);
    };
    reader.onerror = () => {
      this.erro = 'Não foi possível ler o arquivo. Tente novamente.';
    };
    reader.readAsDataURL(this.arquivo);
  }

  private emitir(numero: string, base64: string | null, nome: string | null): void {
    this.confirmado.emit({
      ...this.desenho,
      produtoId: this.produtoId,
      numeroDesenho: numero,
      versao: String(this.versaoNumero(this.desenho.versao)),
      observacao: (this.desenho.observacao || '').trim() || null,
      anexo: base64,
      anexoNome: nome
    });

    this.fecharModal();
  }

  private novo(): ProdutoDesenho {
    return {
      id: 0,
      produtoId: this.produtoId,
      numeroDesenho: '',
      versao: null,
      observacao: null,
      padrao: false,
      ativo: true,
      anexo: null,
      anexoNome: null,
      isCreated: true,
      isUpdated: false,
      isDeleted: false
    };
  }

  private limparArquivo(): void {
    this.arquivo = null;
    const input = document.getElementById(`${this.idModal}-arquivo`) as HTMLInputElement;
    if (input) input.value = '';
  }
}
